import { useState } from "react";
import { Popover } from "@mui/material";
import { useTranslation } from "@i18n";
import { QUICK_EMOJIS, EMOJI_GROUPS, readRecentEmojis, pushRecentEmoji } from "../core/emojiData";
import type { PortalChatStorage } from "../core/platform";

// The grid, quick-reaction row and recents logic live in the shared core (mobile renders the same
// data in a bottom sheet); this file is only the web Popover shell.
export { QUICK_EMOJIS };

/** Recents persist per browser (not per tab) — localStorage, unlike the panel's sessionStorage. */
const recentsStorage: PortalChatStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // localStorage may be unavailable; recents are cosmetic.
    }
  },
};

type EmojiPickerProps = {
  readonly anchorEl: HTMLElement | null;
  readonly onClose: () => void;
  readonly onPick: (emoji: string) => void;
};

/** Compact dependency-free emoji picker (searchless grid over curated groups). */
export function EmojiPicker({ anchorEl, onClose, onPick }: EmojiPickerProps) {
  const { t } = useTranslation();
  const groupLabel = (label: string) =>
    label === "Smileys" ? t("Smileys") : label === "Gestures" ? t("Gestures") : t("Objects");
  const [recent, setRecent] = useState<string[]>(() => readRecentEmojis(recentsStorage));

  const pick = (emoji: string) => {
    setRecent(pushRecentEmoji(recentsStorage, recent, emoji));
    onPick(emoji);
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{ zIndex: 1400 }}
    >
      <div className="max-h-72 w-72 overflow-y-auto p-2">
        {recent.length > 0 && (
          <div>
            <div className="px-1 pb-1 text-xs font-semibold text-gray-400">{t("Recently used")}</div>
            <div className="grid grid-cols-8">
              {recent.map((emoji) => (
                <button
                  key={`recent-${emoji}`}
                  type="button"
                  className="rounded p-1 text-lg hover:bg-[#f0f5f9]"
                  onClick={() => pick(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
        {EMOJI_GROUPS.map((group) => (
          <div key={group.label}>
            <div className="px-1 pb-1 pt-2 text-xs font-semibold text-gray-400">{groupLabel(group.label)}</div>
            <div className="grid grid-cols-8">
              {group.emojis.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="rounded p-1 text-lg hover:bg-[#f0f5f9]"
                  onClick={() => pick(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Popover>
  );
}
